// src/components/NavbarMinimal.jsx

import React from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  Center, 
  Stack, 
  Tooltip, 
  UnstyledButton, 
  Avatar, 
  Text,
  Divider,
  rem
} from "@mantine/core";
import classes from "./NavbarMinimal.module.css";
import PropTypes from "prop-types";

// --- Impor Ikon ---
import {
  IconGauge,
  IconUser,
  IconLogout,
  IconMapPin,
  IconToolsKitchen2,
  IconTruckDelivery,
  IconClipboardList,
  IconFileText,
  IconStethoscope,
  IconSchool
} from "@tabler/icons-react";

// --- Komponen Link Sidebar ---
function NavbarLink({
  icon: Icon,
  label,
  path,
  active,
  onClick,
}) {
  return (
    <Tooltip label={label} position="right" transitionProps={{ duration: 0 }} withArrow>
      <UnstyledButton
        component={Link}
        to={path}
        onClick={onClick}
        className={classes.link}
        data-active={active || undefined}
      >
        <Icon style={{ width: rem(22), height: rem(22) }} stroke={1.5} />
      </UnstyledButton>
    </Tooltip>
  );
}

NavbarLink.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

// --- Daftar Menu ---
const allMenus = [
  // --- ADMIN ---
  { icon: IconGauge, label: "Dashboard", path: "/admin/dashboard", roles: ["admin"] },
  { icon: IconUser, label: "Manajemen User", path: "/admin/users", roles: ["admin"] },
  { icon: IconMapPin, label: "Manajemen Wilayah", path: "/admin/wilayah", roles: ["admin"] },
  { icon: IconToolsKitchen2, label: "Manajemen Menu", path: "/admin/menu", roles: ["admin"] },
  { icon: IconTruckDelivery, label: 'Manajemen Distribusi', path: '/admin/distribusi', roles: ['admin'] },
  { icon: IconClipboardList, label: 'Konfirmasi Penerimaan', path: '/admin/konfirmasi-penerimaan', roles: ['admin'] },
  { icon: IconStethoscope, label: 'Laporan Nutrisi', path: '/admin/laporan-nutrisi', roles: ['admin'] },
  { icon: IconFileText, label: 'Laporan Monitoring', path: '/admin/laporan-monitoring', roles: ['admin'] },

  // --- PENYEDIA ---
  { icon: IconGauge, label: "Dashboard", path: "/penyedia/dashboard", roles: ["penyedia"] },
  { icon: IconToolsKitchen2, label: "Manajemen Menu", path: "/penyedia/menu", roles: ["penyedia"] },
  { icon: IconTruckDelivery, label: 'Manajemen Distribusi', path: '/penyedia/distribusi', roles: ['penyedia'] },

  // --- SUPERVISOR ---
  { icon: IconGauge, label: "Dashboard", path: "/supervisor/dashboard", roles: ["supervisor"] },
  { icon: IconTruckDelivery, label: 'Manajemen Distribusi', path: '/supervisor/distribusi', roles: ['supervisor'] },
  { icon: IconStethoscope, label: 'Laporan Nutrisi', path: '/supervisor/laporan-nutrisi', roles: ['supervisor'] },
  { icon: IconFileText, label: 'Laporan Monitoring', path: '/supervisor/laporan-monitoring', roles: ['supervisor'] },

  // --- SEKOLAH ---
  { icon: IconGauge, label: "Dashboard", path: "/sekolah/dashboard", roles: ["sekolah"] },
  { icon: IconClipboardList, label: 'Konfirmasi Penerimaan', path: '/sekolah/konfirmasi', roles: ['sekolah'] },
];

export function NavbarMinimal() {
  const { user, logout } = useAuth();
  const location = useLocation();
  
  // --- Handling Role yang Aman ---
  let userRole = null;
  if (user?.role) {
      if (typeof user.role === 'string') userRole = user.role.toLowerCase();
      else if (user.role.nama_role) userRole = user.role.nama_role.toLowerCase();
      else if (user.role.name) userRole = user.role.name.toLowerCase();
  }

  // --- Filter Menu ---
  const filteredMenu = allMenus.filter((item) => {
      // Jika userRole null, jangan tampilkan apa-apa
      if (!userRole) return false;
      // Cek apakah array roles di menu mengandung role user
      // Kita pakai toLowerCase() agar case-insensitive
      return item.roles.includes(userRole);
  });

  const links = filteredMenu.map((link) => (
    <NavbarLink
      {...link}
      key={link.path} // Gunakan path sebagai key yang unik
      active={location.pathname === link.path}
    />
  ));

  // Helper Inisial Nama
  const getInitials = (name) => {
    return name ? name.substring(0, 2).toUpperCase() : "US";
  };

  return (
    <nav className={classes.navbar}>
      
      {/* 1. BAGIAN PROFIL USER (Atas) */}
      <Center p="md">
        <Tooltip label={`Halo, ${user?.name || 'User'}!`} position="right" withArrow>
            <Stack align="center" gap={5}>
                <Avatar 
                    color="blue" 
                    radius="xl" 
                    size="md" 
                    variant="filled"
                >
                    {getInitials(user?.name)}
                </Avatar>
                {/* Opsional: Tampilkan Role Singkat */}
                <Text size="10px" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: 0.5 }}>
                    {userRole === 'admin' ? 'ADM' : 
                     userRole === 'supervisor' ? 'SPV' : 
                     userRole === 'penyedia' ? 'MTR' : 
                     userRole === 'sekolah' ? 'SCH' : 'USR'}
                </Text>
            </Stack>
        </Tooltip>
      </Center>

      <Divider my="sm" color="gray.2" />

      {/* 2. BAGIAN MENU NAVIGASI (Tengah - Scrollable) */}
      <div className={classes.navbarMain}>
        <Stack justify="center" gap={8}>
          {links}
        </Stack>
      </div>

      <Divider my="sm" color="gray.2" />

      {/* 3. BAGIAN LOGOUT (Bawah) */}
      <Stack justify="center" gap={0} p="md">
        <Tooltip label="Keluar Aplikasi" position="right" withArrow>
          <UnstyledButton className={classes.link} onClick={logout} data-logout>
            <IconLogout style={{ width: rem(22), height: rem(22) }} stroke={1.5} />
          </UnstyledButton>
        </Tooltip>
      </Stack>
    </nav>
  );
}